// Phase HR-002 — Mobile-first Daily Attendance redesign.
//
// What changed:
//   * Roster view (1 card per active employee) instead of one-by-one drawer
//   * Single tap on the status chip cycles PRESENT → ABSENT → HALF_DAY →
//     ON_LEAVE → PRESENT. No drawer for the 95% case.
//   * "Mark all PRESENT" default — supervisor edits exceptions only.
//   * Bulk save with one parallel batch + one cache invalidation at the
//     end (no refetch storm).
//   * Threshold-coloured KPI tiles using the doc §6 cutoffs (>=96 green /
//     94-95 amber / <94 red, etc).
//   * Village + supervisor surface on every row for cluster pattern
//     spotting that doc §11 calls out (absenteeism by village).
//   * Photos + initials avatars so supervisors recognise people by face.
//   * Sticky bottom action bar with "Save N changes" on mobile, inline on
//     desktop. Reachable in the thumb zone.
//   * Reduced-motion respected.

import { useEffect, useMemo, useState } from 'react'
import {
  Alert, App, Avatar, Badge, Button, DatePicker, Drawer, Empty, Form, Input,
  Modal, Select, Skeleton, Space, Tag, TimePicker, Tooltip, Typography,
} from 'antd'
import {
  CheckOutlined, EditOutlined, ReloadOutlined, SaveOutlined,
  TeamOutlined, WarningOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { attendanceApi, employeesApi } from '@/features/hr/api'
import { departmentsApi } from '@/features/masters/departments/api'
import apiClient from '@/shared/api/client'
import { filterActiveShifts } from '@/shared/constants/shifts'
import type {
  AbsentReasonCode, Attendance, AttendanceStatus, Employee,
} from '@/shared/types/hr'
import type { Shift } from '@/shared/types/master'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import {
  useHrMobilePolish, attendancePctClass, absenteeismPctClass,
  employeeInitials, type ThresholdResult,
} from '@/shared/styles/hrMobilePolish'

const { Text, Title } = Typography

// The five statuses an operator routinely toggles between on the floor.
// COMP_OFF and WEEKLY_OFF are rarer and only reachable via the detail
// drawer, not via the one-tap cycle.
const CYCLE_ORDER: AttendanceStatus[] = ['PRESENT', 'ABSENT', 'HALF_DAY', 'ON_LEAVE']
const ALL_STATUSES: AttendanceStatus[] =
  ['PRESENT', 'ABSENT', 'HALF_DAY', 'ON_LEAVE', 'COMP_OFF', 'WEEKLY_OFF']

const STATUS_CHIP_CLASS: Record<AttendanceStatus, string> = {
  PRESENT:    'hr-status-chip hr-status-chip--present',
  ABSENT:     'hr-status-chip hr-status-chip--absent',
  HALF_DAY:   'hr-status-chip hr-status-chip--half',
  ON_LEAVE:   'hr-status-chip hr-status-chip--leave',
  COMP_OFF:   'hr-status-chip hr-status-chip--compoff',
  WEEKLY_OFF: 'hr-status-chip hr-status-chip--weeklyoff',
}
const STATUS_LABEL: Record<AttendanceStatus, string> = {
  PRESENT:    '✓ Present',
  ABSENT:     '✗ Absent',
  HALF_DAY:   '½ Half',
  ON_LEAVE:   'Leave',
  COMP_OFF:   'Comp',
  WEEKLY_OFF: 'W-Off',
}
const ROW_CLASS: Record<AttendanceStatus, string> = {
  PRESENT:    'hr-roster-card hr-roster-card--present',
  ABSENT:     'hr-roster-card hr-roster-card--absent',
  HALF_DAY:   'hr-roster-card hr-roster-card--half',
  ON_LEAVE:   'hr-roster-card hr-roster-card--leave',
  COMP_OFF:   'hr-roster-card',
  WEEKLY_OFF: 'hr-roster-card',
}

interface RosterRow {
  employee: Employee
  existing: Attendance | null            // server row if already marked
  draft: AttendanceStatus | null         // operator's working state
  dirty: boolean                         // changed from existing/null?
  // Optional fields the operator may have set via the detail drawer
  checkIn: Dayjs | null
  checkOut: Dayjs | null
  remarks: string
  otHours: number | null
  // Phase HR-003 Wave 2
  absentReasonCode: AbsentReasonCode | null
  absentReasonText: string | null
}

export default function AttendancePage() {
  useHrMobilePolish()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const canCreate = usePermission('attendance.create')
  const canUpdate = usePermission('attendance.update')

  // ─── Filters ───────────────────────────────────────────────────────
  const [date, setDate] = useState<Dayjs>(dayjs())
  const [deptId, setDeptId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<AttendanceStatus | 'ALL' | 'UNMARKED'>('ALL')
  const dateStr = date.format('YYYY-MM-DD')

  // ─── Detail drawer (for time / remarks / OT) ───────────────────────
  const [detailRow, setDetailRow] = useState<RosterRow | null>(null)
  const [detailForm] = Form.useForm()

  // ─── Working draft per employee_id ─────────────────────────────────
  // Mutation never touches existing server rows directly — the draft
  // map captures operator's intended state, then we save the dirty rows
  // in one batch.
  type Draft = {
    status: AttendanceStatus
    checkIn: Dayjs | null
    checkOut: Dayjs | null
    remarks: string
    otHours: number | null
    // Phase HR-003 Wave 2 — captured via the reason-chip row when
    // operator marks ABSENT or HALF_DAY. NULL otherwise.
    absentReasonCode?: AbsentReasonCode | null
    absentReasonText?: string | null
  }
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [recentlySaved, setRecentlySaved] = useState(false)

  // ─── Reference data ────────────────────────────────────────────────
  const { data: deptsData } = useQuery({
    queryKey: ['hr-attendance-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500, plant_id: plantId ?? undefined }),
    enabled: !!plantId,
  })
  const { data: shiftsData } = useQuery({
    queryKey: ['hr-attendance-shifts', plantId],
    queryFn: async () => {
      const r = await apiClient.get<Shift[] | { items: Shift[] }>('/shifts',
        { params: plantId ? { plant_id: plantId } : {} })
      const raw = Array.isArray(r.data) ? r.data : (r.data as { items: Shift[] }).items ?? []
      return filterActiveShifts(raw)
    },
    enabled: !!plantId,
  })

  // ─── Employees in scope (entire dept or plant) ─────────────────────
  // The page-level list endpoint already supports plant_id + department_id
  // filters. Capped at 500 active employees — plenty for one dept; we'll
  // paginate only if a future plant needs >500.
  const { data: employeesData, isLoading: empLoading } = useQuery({
    queryKey: ['hr-roster-employees', plantId, deptId],
    queryFn: () => employeesApi.list({
      plant_id: plantId, department_id: deptId,
      is_active: true, page: 1, size: 500,
    }),
    enabled: !!plantId,
  })

  // ─── Existing attendance rows for this date+filter ─────────────────
  // Up to 200 rows (limit of the endpoint). If a dept has >200 marked rows
  // we'd need pagination, but realistically a single dept on a single
  // shift fits well under that.
  const { data: attData, isLoading: attLoading, refetch } = useQuery({
    queryKey: ['hr-roster-attendance', dateStr, deptId, shiftId],
    queryFn: () => attendanceApi.list({
      page: 1, size: 200,
      attendance_date: dateStr,
      department_id: deptId,
      shift_id: shiftId,
    }),
    enabled: !!plantId,
  })

  // ─── Merge: roster = employees LEFT JOIN attendance LEFT JOIN draft ─
  const roster: RosterRow[] = useMemo(() => {
    if (!employeesData?.items) return []
    const attByEmp = new Map<string, Attendance>()
    for (const a of attData?.items ?? []) attByEmp.set(a.employee_id, a)
    return employeesData.items.map((e) => {
      const existing = attByEmp.get(e.id) ?? null
      const d = drafts[e.id]
      const draftStatus = d?.status ?? existing?.status ?? null
      // dirty = operator changed from server state. If no server state
      // and operator hasn't touched it, it's "pending" (not dirty).
      const dirty = d !== undefined && (
        d.status !== existing?.status
        || (existing?.remarks ?? '') !== d.remarks
        || (existing?.check_in_time ?? null) !==
            (d.checkIn ? d.checkIn.toISOString() : null)
        || (existing?.check_out_time ?? null) !==
            (d.checkOut ? d.checkOut.toISOString() : null)
        || Number(existing?.overtime_hours ?? 0) !== (d.otHours ?? 0)
        || (existing?.absent_reason_code ?? null) !== (d.absentReasonCode ?? null)
        || (existing?.absent_reason_text ?? null) !== (d.absentReasonText ?? null)
      )
      return {
        employee: e,
        existing,
        draft: draftStatus,
        dirty,
        checkIn: d?.checkIn ?? (existing?.check_in_time ? dayjs(existing.check_in_time) : null),
        checkOut: d?.checkOut ?? (existing?.check_out_time ? dayjs(existing.check_out_time) : null),
        remarks: d?.remarks ?? existing?.remarks ?? '',
        otHours: d?.otHours ?? (existing ? Number(existing.overtime_hours) || 0 : null),
        absentReasonCode: d?.absentReasonCode ?? existing?.absent_reason_code ?? null,
        absentReasonText: d?.absentReasonText ?? existing?.absent_reason_text ?? null,
      }
    })
  }, [employeesData, attData, drafts])

  // ─── Stats + threshold tiles ───────────────────────────────────────
  const stats = useMemo(() => {
    const total = roster.length
    let present = 0, absent = 0, half = 0, leave = 0, pending = 0
    for (const r of roster) {
      if (!r.draft) pending++
      else if (r.draft === 'PRESENT')  present++
      else if (r.draft === 'ABSENT')   absent++
      else if (r.draft === 'HALF_DAY') half++
      else if (r.draft === 'ON_LEAVE') leave++
    }
    const presentEffective = present + half * 0.5  // half-day counts ½
    const presentPct = total ? Math.round((presentEffective / total) * 100) : null
    const absentPct = total ? Math.round((absent / total) * 100) : null
    const dirtyCount = roster.filter(r => r.dirty).length
    return { total, present, absent, half, leave, pending,
             presentPct, absentPct, dirtyCount }
  }, [roster])

  // ─── Filtered roster (by status filter chip) ───────────────────────
  const visibleRoster = useMemo(() => {
    if (statusFilter === 'ALL') return roster
    if (statusFilter === 'UNMARKED') return roster.filter(r => !r.draft)
    return roster.filter(r => r.draft === statusFilter)
  }, [roster, statusFilter])

  // ─── Status cycle (one tap on chip) ────────────────────────────────
  function cycleStatus(row: RosterRow) {
    if (!canCreate && !canUpdate) return
    const current = row.draft
    const idx = current ? CYCLE_ORDER.indexOf(current) : -1
    const next = CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length]
    setStatus(row, next)
  }
  function setStatus(row: RosterRow, status: AttendanceStatus) {
    setDrafts(prev => {
      const existing = prev[row.employee.id]
      // If the operator moves back to PRESENT/COMP_OFF/WEEKLY_OFF, clear
      // any stale absent reason — keeping it would confuse the report.
      const carryReason = status === 'ABSENT' || status === 'HALF_DAY'
      return {
        ...prev,
        [row.employee.id]: {
          status,
          checkIn: existing?.checkIn ?? row.checkIn,
          checkOut: existing?.checkOut ?? row.checkOut,
          remarks: existing?.remarks ?? row.remarks,
          otHours: existing?.otHours ?? row.otHours,
          absentReasonCode: carryReason
            ? (existing?.absentReasonCode ?? row.absentReasonCode)
            : null,
          absentReasonText: carryReason
            ? (existing?.absentReasonText ?? row.absentReasonText)
            : null,
        },
      }
    })
  }

  function setAbsentReason(row: RosterRow, code: AbsentReasonCode | null, text?: string) {
    if (!canCreate && !canUpdate) return
    setDrafts(prev => {
      const existing = prev[row.employee.id]
      return {
        ...prev,
        [row.employee.id]: {
          status: existing?.status ?? row.draft ?? 'ABSENT',
          checkIn: existing?.checkIn ?? row.checkIn,
          checkOut: existing?.checkOut ?? row.checkOut,
          remarks: existing?.remarks ?? row.remarks,
          otHours: existing?.otHours ?? row.otHours,
          absentReasonCode: code,
          absentReasonText: text ?? existing?.absentReasonText ?? row.absentReasonText,
        },
      }
    })
  }
  function markAllPresent() {
    if (!canCreate) return
    const draftPatch: Record<string, Draft> = {}
    for (const r of roster) {
      // Only set rows that aren't already PRESENT — avoids marking
      // existing non-PRESENT rows as dirty.
      if (r.draft !== 'PRESENT') {
        draftPatch[r.employee.id] = {
          status: 'PRESENT',
          checkIn: r.checkIn, checkOut: r.checkOut,
          remarks: r.remarks, otHours: r.otHours,
        }
      }
    }
    setDrafts(prev => ({ ...prev, ...draftPatch }))
    message.success(`Defaulted ${Object.keys(draftPatch).length} workers to PRESENT — review exceptions and save.`)
  }

  // ─── Bulk save: parallel API calls + one invalidation at the end ───
  // To avoid the refetch storm we saw on RMQualityPage, we DON'T
  // invalidate inside each mutation's onSuccess. Instead we collect all
  // results, then refetch once.
  const saveMut = useMutation({
    mutationFn: async () => {
      const dirty = roster.filter(r => r.dirty)
      if (dirty.length === 0) {
        throw new Error('Nothing to save')
      }
      if (!shiftId) {
        throw new Error('Pick a shift before saving')
      }
      // Note: we deliberately do NOT require a page-level deptId here.
      // Each employee row carries its own department_id, so we use
      // that. Earlier behavior blocked save whenever the operator was
      // on "All departments" view — but that's the most common case
      // when an HR clerk is marking attendance across the floor.
      const results = await Promise.allSettled(dirty.map(r => {
        const carryReason = r.draft === 'ABSENT' || r.draft === 'HALF_DAY'
        const body = {
          attendance_date: dateStr,
          employee_id: r.employee.id,
          // Use the employee's own department, not the page filter.
          // Falls back to the page filter only if the employee record
          // somehow lacks one (shouldn't happen but defensive).
          department_id: r.employee.department_id || deptId,
          shift_id: shiftId,
          status: r.draft!,
          check_in_time: r.checkIn ? r.checkIn.toISOString() : null,
          check_out_time: r.checkOut ? r.checkOut.toISOString() : null,
          overtime_hours: r.otHours ?? 0,
          remarks: r.remarks || null,
          // Phase HR-003 Wave 2 — Absenteeism Study reason. Cleared when
          // status returns to PRESENT/leave, kept only for ABSENT/HALF_DAY.
          absent_reason_code: carryReason ? r.absentReasonCode : null,
          absent_reason_text:
            carryReason && r.absentReasonCode === 'OTHER' ? r.absentReasonText : null,
        }
        return r.existing
          ? attendanceApi.update(r.existing.id, body)
          : attendanceApi.create(body)
      }))
      const failed = results.filter(x => x.status === 'rejected').length
      return { ok: results.length - failed, failed }
    },
    onSuccess: ({ ok, failed }) => {
      // ONE invalidation at the end, not N. Doc-anti-pattern-aware.
      qc.invalidateQueries({ queryKey: ['hr-roster-attendance', dateStr, deptId, shiftId] })
      setDrafts({})
      setRecentlySaved(true)
      setTimeout(() => setRecentlySaved(false), 800)
      if (failed === 0) {
        message.success(`Saved ${ok} attendance record${ok > 1 ? 's' : ''}.`)
      } else {
        message.warning(`Saved ${ok} of ${ok + failed}. ${failed} failed — check the rows that stayed orange.`)
      }
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  })

  // ─── Detail drawer save (single-row edits for time/OT/remarks) ─────
  function openDetail(row: RosterRow) {
    setDetailRow(row)
    detailForm.setFieldsValue({
      status: row.draft ?? 'PRESENT',
      check_in_time: row.checkIn,
      check_out_time: row.checkOut,
      overtime_hours: row.otHours ?? 0,
      remarks: row.remarks,
    })
  }
  function saveDetail(vals: Record<string, unknown>) {
    if (!detailRow) return
    setDrafts(prev => ({
      ...prev,
      [detailRow.employee.id]: {
        status: vals.status as AttendanceStatus,
        checkIn: (vals.check_in_time as Dayjs) ?? null,
        checkOut: (vals.check_out_time as Dayjs) ?? null,
        remarks: (vals.remarks as string) || '',
        otHours: vals.overtime_hours == null ? null : Number(vals.overtime_hours),
      },
    }))
    setDetailRow(null)
  }

  // ─── Auto-reset draft when filters change context ──────────────────
  useEffect(() => {
    // Changing date/dept/shift means a different roster — drop pending
    // drafts so the operator doesn't accidentally save stale changes.
    setDrafts({})
  }, [dateStr, deptId, shiftId])

  const deptOptions = (deptsData?.items ?? []).map((d) => ({
    label: d.name, value: d.id,
  }))
  const shiftOptions = (shiftsData ?? []).map((s) => ({
    label: `${s.shift_code} — ${s.shift_name}`, value: s.id,
  }))
  const isLoading = empLoading || attLoading

  return (
    <div className="hr-mobile-root">
      <PageHeader
        title="Daily Attendance"
        subtitle="Mark and review attendance per shift. Tap status chip to cycle — long-press or use edit for time/remarks."
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        }
      />

      {/* ── Date + filter chips ──────────────────────────────────────── */}
      <Space wrap style={{ marginBottom: 12, width: '100%' }}>
        <DateChipRow value={date} onChange={setDate} />
        <Select
          placeholder="Department"
          allowClear
          options={deptOptions}
          value={deptId}
          onChange={setDeptId}
          style={{ minWidth: 180 }}
          size="large"
          showSearch
          optionFilterProp="label"
        />
        <Select
          placeholder="Shift"
          allowClear
          options={shiftOptions}
          value={shiftId}
          onChange={setShiftId}
          style={{ minWidth: 160 }}
          size="large"
          showSearch
          optionFilterProp="label"
        />
      </Space>

      {/* ── KPI tiles (doc §6 thresholds) ────────────────────────────── */}
      <Space wrap style={{ marginBottom: 12, width: '100%', display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
        <KpiTile
          label="Roster"
          value={stats.total}
          tone="neutral"
          subtitle={`${stats.pending} unmarked`}
        />
        <KpiTile
          label="Present %"
          value={stats.presentPct ?? '—'}
          suffix="%"
          tone={attendancePctClass(stats.presentPct)}
          subtitle={`${stats.present} workers`}
        />
        <KpiTile
          label="Absent %"
          value={stats.absentPct ?? '—'}
          suffix="%"
          tone={absenteeismPctClass(stats.absentPct)}
          subtitle={`${stats.absent} absent`}
        />
        <KpiTile
          label="On Leave"
          value={stats.leave}
          tone={stats.leave > 0 ? 'amber' : 'neutral'}
        />
        <KpiTile
          label="Half Day"
          value={stats.half}
          tone={stats.half > 0 ? 'amber' : 'neutral'}
        />
      </Space>

      {/* ── Status filter chips ──────────────────────────────────────── */}
      <div className="hr-filter-chips" style={{ marginBottom: 12 }}>
        {[
          { v: 'ALL',      label: `All (${stats.total})` },
          { v: 'UNMARKED', label: `Unmarked (${stats.pending})` },
          { v: 'PRESENT',  label: `Present (${stats.present})` },
          { v: 'ABSENT',   label: `Absent (${stats.absent})` },
          { v: 'HALF_DAY', label: `Half (${stats.half})` },
          { v: 'ON_LEAVE', label: `Leave (${stats.leave})` },
        ].map(({ v, label }) => (
          <button
            key={v}
            type="button"
            className={'hr-filter-chip' + (statusFilter === v ? ' hr-filter-chip--active' : '')}
            onClick={() => setStatusFilter(v as typeof statusFilter)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Setup hints ──────────────────────────────────────────────── */}
      {!deptId && (
        <Alert
          type="info"
          showIcon
          message="Pick a department to load the roster"
          description="Showing all employees across the plant. Filter by department + shift for the most useful view."
          style={{ marginBottom: 12 }}
        />
      )}
      {deptId && !shiftId && (
        <Alert
          type="warning"
          showIcon
          message="Pick a shift before saving"
          description="Attendance entries need a shift. Set the shift filter above; you can change it per-row later via the edit drawer if needed."
          style={{ marginBottom: 12 }}
        />
      )}

      {/* ── Roster cards ─────────────────────────────────────────────── */}
      {isLoading ? (
        <Skeleton active />
      ) : visibleRoster.length === 0 ? (
        <Empty description={
          stats.total === 0
            ? 'No active employees match the filters'
            : 'No employees match this status filter'
        } />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {visibleRoster.map(row => (
            <RosterCard
              key={row.employee.id}
              row={row}
              onCycle={cycleStatus}
              onOpenDetail={openDetail}
              onReason={setAbsentReason}
            />
          ))}
        </div>
      )}

      {/* ── Sticky bottom action bar ─────────────────────────────────── */}
      {(stats.dirtyCount > 0 || stats.pending > 0) && (
        <div className="hr-sticky-actions">
          <Button
            size="large"
            icon={<TeamOutlined />}
            onClick={markAllPresent}
            disabled={stats.pending === 0}
          >
            Mark all Present ({stats.pending})
          </Button>
          <Tooltip
            title={
              !shiftId
                ? 'Pick a shift first — every attendance row needs a shift.'
                : stats.dirtyCount === 0
                  ? 'Nothing to save — tap a row to mark attendance.'
                  : ''
            }
          >
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              onClick={() => saveMut.mutate()}
              loading={saveMut.isPending}
              // Only block on shift + dirty count. Department comes
              // from each employee's own record at save time, so the
              // page-level dept filter is optional.
              disabled={stats.dirtyCount === 0 || !shiftId}
              className={recentlySaved ? 'hr-save-success' : undefined}
            >
              Save {stats.dirtyCount > 0 ? `(${stats.dirtyCount})` : ''}
            </Button>
          </Tooltip>
        </div>
      )}

      {/* ── Detail drawer for time / OT / remarks ────────────────────── */}
      <Drawer
        title={detailRow ? `${detailRow.employee.emp_name} — ${detailRow.employee.emp_code}` : ''}
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        width={400}
        destroyOnClose
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setDetailRow(null)}>Cancel</Button>
            <Button type="primary" onClick={() => detailForm.submit()}>
              Apply
            </Button>
          </Space>
        }
      >
        <Form
          form={detailForm}
          layout="vertical"
          onFinish={saveDetail}
        >
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={ALL_STATUSES.map(s => ({ label: s.replace('_', ' '), value: s }))}
              size="large"
            />
          </Form.Item>
          <Form.Item name="check_in_time" label="Check In">
            <TimePicker format="HH:mm" style={{ width: '100%' }} size="large" />
          </Form.Item>
          <Form.Item name="check_out_time" label="Check Out">
            <TimePicker format="HH:mm" style={{ width: '100%' }} size="large" />
          </Form.Item>
          <Form.Item name="overtime_hours" label="Overtime Hours">
            <Input type="number" inputMode="decimal" step={0.25} min={0} size="large" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────

function DateChipRow({ value, onChange }: {
  value: Dayjs
  onChange: (d: Dayjs) => void
}) {
  const today = dayjs().startOf('day')
  const isToday    = value.isSame(today, 'day')
  const isYesterday = value.isSame(today.subtract(1, 'day'), 'day')
  const isTomorrow  = value.isSame(today.add(1, 'day'), 'day')
  return (
    <Space size={4} wrap>
      <button type="button"
        className={'hr-filter-chip' + (isYesterday ? ' hr-filter-chip--active' : '')}
        onClick={() => onChange(today.subtract(1, 'day'))}>
        Yesterday
      </button>
      <button type="button"
        className={'hr-filter-chip' + (isToday ? ' hr-filter-chip--active' : '')}
        onClick={() => onChange(today)}>
        Today
      </button>
      <button type="button"
        className={'hr-filter-chip' + (isTomorrow ? ' hr-filter-chip--active' : '')}
        onClick={() => onChange(today.add(1, 'day'))}>
        Tomorrow
      </button>
      <DatePicker
        value={value}
        onChange={(d) => d && onChange(d)}
        format="DD-MMM-YYYY"
        allowClear={false}
        size="large"
        style={{ width: 150 }}
      />
    </Space>
  )
}

function KpiTile({ label, value, suffix, tone, subtitle }: {
  label: string
  value: number | string
  suffix?: string
  tone: ThresholdResult
  subtitle?: string
}) {
  return (
    <div className={`threshold-tile threshold-tile--${tone}`}>
      <div className="threshold-tile__label">{label}</div>
      <div className="threshold-tile__value">
        {value}{suffix && <span style={{ fontSize: 16, marginLeft: 2 }}>{suffix}</span>}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
          {subtitle}
        </div>
      )}
    </div>
  )
}

// Phase HR-003 Wave 2 — Absenteeism Study chip strip. Doc §11.1 root-cause
// codes. Shown inline below the row when status = ABSENT / HALF_DAY.
const ABSENT_REASON_OPTIONS: { code: AbsentReasonCode; label: string }[] = [
  { code: 'UNINFORMED',    label: 'Uninformed' },
  { code: 'SICK_LEAVE',    label: 'Sick' },
  { code: 'FESTIVAL',      label: 'Festival' },
  { code: 'AGRICULTURAL',  label: 'Agri' },
  { code: 'FAMILY',        label: 'Family' },
  { code: 'TRANSPORT',     label: 'Transport' },
  { code: 'WAGE_RELATED',  label: 'Wage' },
  { code: 'OTHER',         label: 'Other…' },
]

function RosterCard({ row, onCycle, onOpenDetail, onReason }: {
  row: RosterRow
  onCycle: (r: RosterRow) => void
  onOpenDetail: (r: RosterRow) => void
  onReason: (r: RosterRow, code: AbsentReasonCode | null, text?: string) => void
}) {
  const e = row.employee
  const status = row.draft
  const cls = (status ? ROW_CLASS[status] : 'hr-roster-card')
              + (row.dirty ? ' hr-roster-card--dirty' : '')
  // Reason chip row appears only for absent-like statuses. Doc-aligned.
  const showReasonRow = status === 'ABSENT' || status === 'HALF_DAY'
  // Repeat-absentee badge: server flagged this employee as ≥3 absences this
  // month. Doc §7 sample column "Repeat Absence Count".
  const repeatCount = row.existing?.repeat_count_month ?? 0
  const isRepeat = (row.existing?.is_repeat_absentee ?? false) || repeatCount >= 3
  // Call-pending indicator: HR hasn't yet logged a verification call for
  // today's ABSENT row. Doc §11.1 countermeasure.
  const callPending = status === 'ABSENT' && row.existing
    && row.existing.status === 'ABSENT' && !row.existing.call_verified_at
  return (
    <div className={cls} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
        <Avatar
          size={44}
          src={e.photo_url ?? undefined}
          className="hr-avatar"
          style={!e.photo_url ? {
            background: hashColor(e.id),
          } : undefined}
        >
          {!e.photo_url && employeeInitials(e.emp_name)}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}
             onClick={() => onCycle(row)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Text strong style={{ fontSize: 14, lineHeight: 1.2 }}>{e.emp_name}</Text>
            <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
              {e.emp_code}
            </Text>
            {isRepeat && (
              <Tag color="red" style={{ fontSize: 10, padding: '0 6px',
                                        marginInlineEnd: 0, lineHeight: '18px' }}>
                Repeat {repeatCount}×
              </Tag>
            )}
            {callPending && (
              <Tag color="orange" style={{ fontSize: 10, padding: '0 6px',
                                            marginInlineEnd: 0, lineHeight: '18px' }}>
                Call pending
              </Tag>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                        marginTop: 3, flexWrap: 'wrap' }}>
            {e.village && (
              <Tag color="purple" style={{ fontSize: 10, padding: '0 6px',
                                            marginInlineEnd: 0, lineHeight: '18px' }}>
                {e.village}
              </Tag>
            )}
            {e.shift_type && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {e.shift_type}
              </Text>
            )}
            {e.reporting_supervisor_name && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                · Sup: {e.reporting_supervisor_name}
              </Text>
            )}
          </div>
        </div>
        <Space size={6}>
          {status ? (
            <button
              type="button"
              className={STATUS_CHIP_CLASS[status]}
              onClick={() => onCycle(row)}
              aria-label={`Status: ${status}. Tap to cycle.`}
            >
              {STATUS_LABEL[status]}
            </button>
          ) : (
            <button
              type="button"
              className="hr-status-chip hr-status-chip--pending"
              onClick={() => onCycle(row)}
              aria-label="Not marked. Tap to set Present."
            >
              Tap to set
            </button>
          )}
          <Tooltip title="Time / remarks / OT">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onOpenDetail(row)}
              style={{ height: 36 }}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Phase HR-003 Wave 2 — reason chip strip. Shown only when absent. */}
      {showReasonRow && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4,
                      flexWrap: 'wrap', marginTop: 10,
                      paddingTop: 10, borderTop: '1px dashed #e8e8e8' }}>
          <Text type="secondary" style={{ fontSize: 11, marginRight: 4,
                                          fontWeight: 600, letterSpacing: 0.5 }}>
            REASON:
          </Text>
          {ABSENT_REASON_OPTIONS.map((opt) => {
            const selected = row.absentReasonCode === opt.code
            return (
              <button
                key={opt.code}
                type="button"
                className={`hr-filter-chip${selected ? ' hr-filter-chip--active' : ''}`}
                style={{ height: 28, padding: '0 10px', fontSize: 11 }}
                onClick={() => onReason(row, selected ? null : opt.code)}
                aria-pressed={selected}
              >
                {opt.label}
              </button>
            )
          })}
          {row.absentReasonCode === 'OTHER' && (
            <Input
              size="small"
              placeholder="Specify reason"
              value={row.absentReasonText ?? ''}
              onChange={(ev) => onReason(row, 'OTHER', ev.target.value)}
              style={{ width: 180, marginLeft: 4 }}
            />
          )}
        </div>
      )}
    </div>
  )
}

// Deterministic colour per employee id so the initials avatar feels
// stable across reloads. Eight HSL hues spaced 45° apart.
function hashColor(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  const hue = (h % 8) * 45
  return `linear-gradient(135deg, hsl(${hue} 60% 55%) 0%, hsl(${hue} 60% 40%) 100%)`
}
